import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Form } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import TreatmentPlanPrintView from '../TreatmentPlan/TreatmentPlanPrintView'
import { useAppSelector } from '@store/hooks'
import { IDentalChart, IPerioChart } from '@dataTypes/chartingFeature.types'
import { ITreatmentPlan } from '@dataTypes/treatmentPlan.types'
import { IClinic } from '@dataTypes/clinic.type'
import { ProviderDetails } from '@dataTypes/appointment.type'
import { getFullName } from '@utils/fullname.util'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import { calculateAge } from '@utils/dob.utils'
import { formatNumber } from '@utils/numberFormatter.util'
import { getFullAddress } from '@utils/address.util'
import { useReactToPrint } from 'react-to-print'
import { buildPerioSitesByTooth } from '@utils/perio-line.utils'
import ToothChart from '../Dental/ToothChart.component'
import './ChartingModals.css'

const DEFAULT_CLINIC_LOGO_URL = '/clinic.png'

type ProviderNameSource = {
  provider_id?: string
  provider?: ProviderDetails | null
} | null

const collectPrintProviderNames = (
  sources: ProviderNameSource[],
  providerLookup: Array<{
    provider_id: string
    first_name: string
    middle_name?: string
    last_name: string
    work_role?: string
  }>,
): string => {
  const byId = new Map<string, string>()

  sources.forEach((source) => {
    const providerId = source?.provider_id?.trim()
    if (!providerId || byId.has(providerId)) {
      return
    }

    const resolved =
      source?.provider ??
      providerLookup.find((provider) => provider.provider_id === providerId)
    const displayName = formatProviderDisplayName(resolved, '')
    if (displayName) {
      byId.set(providerId, displayName)
    }
  })

  return Array.from(byId.values()).join(', ')
}

const getClinicLogoSrc = (clinic: IClinic | null | undefined): string => {
  const url = clinic?.logo_url?.trim()
  return url ? url : DEFAULT_CLINIC_LOGO_URL
}

interface ClinicReportHeaderProps {
  clinic: IClinic | null | undefined
}

const ClinicReportHeader: React.FC<ClinicReportHeaderProps> = ({ clinic }) => {
  return (
    <div className="modal-charting-report-header mb-2">
      <div className="d-flex justify-start flex-row">
        <div className="inline-flex dash-logo p-0 w-auto">
          <img
            src={getClinicLogoSrc(clinic)}
            width="38"
            height="38"
            className="d-inline-block align-middle me-2"
            alt={clinic?.clinic_name || 'Clinic'}
            onError={(e) => {
              e.currentTarget.onerror = null
              e.currentTarget.src = DEFAULT_CLINIC_LOGO_URL
            }}
          />
        </div>
        <div className="mcrh-left-inner">
          <div>{clinic?.clinic_name || 'Clinic'}</div>
          <div>
            Address:{' '}
            {getFullAddress({
              address: clinic?.address,
              city: clinic?.city,
              state: clinic?.state,
              zip: clinic?.zip,
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

interface PrintPdfModalProps {
  show: boolean
  onHide: () => void
}

const PrintPdfModal: React.FC<PrintPdfModalProps> = ({ show, onHide }) => {
  const perioCharts = useAppSelector((state) => state.charting.perioCharts)
  const dentalCharts = useAppSelector((state) => state.charting.dentalCharts)
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const providerList = useAppSelector((state) => state.provider.data)

  const [printData, setPrintData] = useState<{
    perioChart?: IPerioChart | undefined
    dentalChart?: IDentalChart | undefined
    treatmentPlan?: ITreatmentPlan | undefined
  }>()

  useEffect(() => {
    if (!show) {
      setPrintData({})
      onHide()
    } else {
      setPrintData({
        perioChart: selectedPerioChart || undefined,
        dentalChart: selectedDentalChart || undefined,
        treatmentPlan: undefined,
      })
    }
  }, [show, selectedPerioChart, selectedDentalChart, onHide])

  const showDentalChart = Boolean(printData?.dentalChart)
  const showPerioChart = Boolean(printData?.perioChart)
  const showTreatmentPlan = Boolean(printData?.treatmentPlan)
  const hasPrintableContent =
    showDentalChart || showPerioChart || showTreatmentPlan
  const hasChartContent = showDentalChart || showPerioChart
  // Only force TP onto a second print page when charts are also included.
  const treatmentOnSeparatePage = hasChartContent && showTreatmentPlan

  const printProviderNames = useMemo((): string => {
    const sources: ProviderNameSource[] = []

    if (showDentalChart && printData?.dentalChart) {
      sources.push({
        provider_id: printData.dentalChart.provider_id,
        provider: printData.dentalChart.provider,
      })
    }

    if (showPerioChart && printData?.perioChart) {
      sources.push({
        provider_id: printData.perioChart.provider_id,
        provider: printData.perioChart.provider,
      })
    }

    if (showTreatmentPlan && printData?.treatmentPlan?.phases) {
      printData.treatmentPlan.phases.forEach((phase) => {
        phase.items.forEach((item) => {
          if (item.provider_id) {
            sources.push({ provider_id: item.provider_id })
          }
        })
      })
    }

    return collectPrintProviderNames(sources, providerList)
  }, [
    showDentalChart,
    showPerioChart,
    showTreatmentPlan,
    printData?.dentalChart,
    printData?.perioChart,
    printData?.treatmentPlan,
    providerList,
  ])

  const renderTreatmentPlanSection = (): React.ReactNode => {
    if (!showTreatmentPlan || !printData?.treatmentPlan) {
      return null
    }

    return (
      <div className="print-treatment-plan-container">
        <div className="modal-charting-report-title mb-2">Treatment plan</div>
        <div className="print-treatment-plan">
          <TreatmentPlanPrintView plan={printData.treatmentPlan} />
        </div>
      </div>
    )
  }

  const printRef = useRef<HTMLDivElement>(null)

  const printChart = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'Charting report',
    onAfterPrint: () => {
      onHide()
    },
  })

  const handlePrint = (): void => {
    printChart()
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      key="create-pdf-modal"
      className="create-pdf-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Print chart</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-2">
        <div className="d-flex flex-row h-100">
          <div className="p-0 overflow-auto create-pdf-modal-box">
            <div ref={printRef} className="pdf-print-container">
              <div className="bg-white print-page w-100">
                <ClinicReportHeader clinic={selectedClinic} />

                <div className="p-2 pt-0 print-page-container">
                  <div className="modal-charting-report-title fs-5">
                    Charting report
                  </div>
                  <div
                    className="d-flex flex-column mt-2 mb-2 gx-3"
                    style={{ fontSize: '12px' }}
                  >
                    <div className="d-flex gap-2">
                      <div>
                        <span className="print-label">Patient name:</span>
                        <span className="print-value">
                          {getFullName(selectedPatient)}
                        </span>
                      </div>
                      <div>
                        <span className="print-label">Gender:</span>
                        <span className="print-value">
                          {selectedPatient?.gender}
                        </span>
                      </div>
                      <div>
                        <span className="print-label">Age:</span>
                        <span className="print-value">
                          {calculateAge(selectedPatient?.date_of_birth ?? '')}
                        </span>
                      </div>
                    </div>
                    {printProviderNames ? (
                      <div className="d-flex gap-2">
                        <div>
                          <span className="print-label">Provider:</span>
                          <span className="print-value">
                            {printProviderNames}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {showDentalChart && printData?.dentalChart && (
                    <div className="print-chart-container">
                      <div className="modal-charting-report-title">
                        Dental chart
                      </div>
                      <div className="w-100 print-tooth-chart px-3">
                        <ToothChart
                          dentalData={printData.dentalChart.dental_data || []}
                          selectedTooth={null}
                          setSelectedTooth={() => {}}
                          view="full"
                          disableLoadingGate={true}
                        />
                      </div>
                    </div>
                  )}

                  {showPerioChart && printData?.perioChart && (
                    <div className="print-chart-container print-perio-container">
                      <div className="modal-charting-report-title">
                        Perio chart
                      </div>
                      <div className="w-100 print-tooth-chart px-3">
                        <ToothChart
                          dentalData={printData?.dentalChart?.dental_data || []}
                          selectedTooth={null}
                          setSelectedTooth={() => {}}
                          isPerio={true}
                          perioSitesByTooth={buildPerioSitesByTooth(
                            printData?.perioChart?.perio_data,
                          )}
                          view="full"
                          disableLoadingGate={true}
                        />
                      </div>
                    </div>
                  )}

                  {!treatmentOnSeparatePage && renderTreatmentPlanSection()}
                </div>
              </div>

              {treatmentOnSeparatePage && (
                <div className="bg-white print-page w-100">
                  <ClinicReportHeader clinic={selectedClinic} />

                  <div className="p-2 pt-0">{renderTreatmentPlanSection()}</div>
                </div>
              )}

              <style>
                {`
          @page { size: A4; margin: 12mm; }
          @media print {
            body { background: white; }
            .pdf-print-container,
            .pdf-print-container * {
              visibility: visible !important;
            }
            .pdf-print-container { position: static !important; }
            .print-page {
              break-after: page;
              position: static !important;
              z-index: auto !important;
              display: block !important;
              min-height: auto !important;
              width: auto !important;
            }
          }
        `}
              </style>
            </div>
          </div>
          <div className="d-flex flex-column">
            <div className="create-pdf-modal-form-content d-flex flex-column flex-grow-1">
              <div className="mb-3">
                <div>
                  <Form.Label>Dental charts</Form.Label>
                  <Form.Select
                    className="pe-5"
                    value={printData?.dentalChart?.chart_id}
                    onChange={(e) =>
                      setPrintData({
                        ...printData,
                        dentalChart:
                          dentalCharts.find(
                            (chart) => chart.chart_id === e.target.value,
                          ) ?? undefined,
                      })
                    }
                  >
                    <option value="">Select a dental chart...</option>
                    {dentalCharts.map((chart) => (
                      <option key={chart.chart_id} value={chart.chart_id}>
                        {chart.title} -{' '}
                        {new Date(
                          chart.date_time_created_at ?? Date.now(),
                        ).toLocaleDateString()}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div className="mb-3">
                <div>
                  <Form.Label>Perio charts</Form.Label>
                  <Form.Select
                    className="pe-5"
                    value={printData?.perioChart?.chart_id}
                    onChange={(e) =>
                      setPrintData({
                        ...printData,
                        perioChart:
                          perioCharts.find(
                            (chart) => chart.chart_id === e.target.value,
                          ) ?? undefined,
                      })
                    }
                  >
                    <option value="">Select a perio chart...</option>
                    {perioCharts.map((chart) => (
                      <option key={chart.chart_id} value={chart.chart_id}>
                        {chart.title} -{' '}
                        {new Date(
                          chart.date_time_created_at ?? Date.now(),
                        ).toLocaleDateString()}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div className="mb-3">
                <div>
                  <Form.Label>Treatment plan(s)</Form.Label>
                  <Form.Select
                    className="pe-5"
                    value={printData?.treatmentPlan?.tp_id}
                    onChange={(e) =>
                      setPrintData({
                        ...printData,
                        treatmentPlan:
                          treatmentPlans.find(
                            (plan) => plan.tp_id === e.target.value,
                          ) ?? undefined,
                      })
                    }
                  >
                    <option value="">Select a treatment plan...</option>
                    {treatmentPlans.map((plan) => (
                      <option key={plan.tp_id} value={plan.tp_id}>
                        {plan.tp_heading} -{' '}
                        {new Date(
                          plan.date_time_created_at ?? Date.now(),
                        ).toLocaleDateString()}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              {printData?.treatmentPlan && (
                <div className="mt-auto pt-3">
                  <div className="plan-pricing-title">Plan pricing</div>
                  {(() => {
                    const totals = printData.treatmentPlan.phases?.reduce(
                      (acc, phase) =>
                        phase.items.reduce(
                          (sum, item) => ({
                            charges: sum.charges + item.charges,
                            insurance: sum.insurance + item.insurance,
                            adjustments: sum.adjustments + item.adjustments,
                            outstanding: sum.outstanding + item.outstanding,
                          }),
                          acc,
                        ),
                      {
                        charges: 0,
                        insurance: 0,
                        adjustments: 0,
                        outstanding: 0,
                      },
                    )

                    return (
                      <div className="d-flex flex-column">
                        <div className="plan-pricing-details">
                          <div>Treatment plan</div>
                          <span>
                            {formatNumber(totals?.charges ?? 0, true)}
                          </span>
                        </div>
                        <div className="plan-pricing-details">
                          <div>Insurance covered</div>
                          <span>
                            {formatNumber(totals?.insurance ?? 0, true)}
                          </span>
                        </div>
                        <div className="plan-pricing-details">
                          <div>Adjustments</div>
                          <span>
                            {formatNumber(totals?.adjustments ?? 0, true)}
                          </span>
                        </div>
                        <hr />
                        <div className="plan-pricing-details">
                          <div>
                            <strong>Total due</strong>
                          </div>
                          <span>
                            {formatNumber(totals?.outstanding ?? 0, true)}
                          </span>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onHide} className="button-secondary me-1">
          Cancel
        </Button>
        <Button
          onClick={handlePrint}
          className="button-primary"
          disabled={!hasPrintableContent}
        >
          Print
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PrintPdfModal
